import { useEffect, useState } from "react";
import { Alert, Button, OTPInput, CheckCircle2, ShieldCheck } from "bizpandit-ui";

import { useSendPhoneOtp, useVerifyPhoneOtp, toE164India } from "@/features/auth/hooks/usePhoneOtp";
import type { PhoneVerificationIntent } from "@/features/auth/types";

interface Props {
  mobile: string;
  intent: PhoneVerificationIntent;
  onVerified: () => void;
  onCancel?: () => void;
  autoSend?: boolean;
}

const OTP_LENGTH = 6;
const DEFAULT_RESEND_COOLDOWN_MS = 30_000;

export default function OtpVerifyPanel({
  mobile,
  intent,
  onVerified,
  onCancel,
  autoSend = true,
}: Props) {
  const sendOtp = useSendPhoneOtp();
  const verifyOtp = useVerifyPhoneOtp();

  const [otp, setOtp] = useState("");
  const [verified, setVerified] = useState(false);
  const [phoneE164, setPhoneE164] = useState<string>(() => toE164India(mobile));
  const [resendAt, setResendAt] = useState<number | null>(null);
  const [, setTick] = useState(0);

  const remainingMs = resendAt == null ? 0 : Math.max(0, resendAt - Date.now());
  const canResend = remainingMs === 0;

  useEffect(() => {
    if (!autoSend) return;
    handleSend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (canResend) return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [canResend]);

  const handleSend = () => {
    sendOtp.mutate(mobile, {
      onSuccess: ({ phone }) => {
        setPhoneE164(phone);
        setResendAt(Date.now() + DEFAULT_RESEND_COOLDOWN_MS);
      },
    });
  };

  const handleVerify = () => {
    if (otp.length !== OTP_LENGTH) return;
    verifyOtp.mutate(
      { phone: phoneE164, code: otp, intent },
      {
        onSuccess: () => {
          setVerified(true);
          onVerified();
        },
      },
    );
  };

  if (verified) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
        <div className="flex items-center gap-2 text-sm font-medium text-emerald-700">
          <CheckCircle2 className="h-4 w-4" />
          Mobile verified — you can complete registration
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border border-border bg-surface p-4">
      <div className="flex items-center gap-2 text-sm font-medium text-navy2">
        <ShieldCheck className="h-4 w-4 text-rose" />
        Verify {phoneE164 || `+${mobile}`}
      </div>

      <p className="text-xs text-navy2/60">
        We sent a 6-digit code to your mobile. Enter it below to confirm the number.
      </p>

      {sendOtp.isError && (
        <Alert variant="destructive">
          {(sendOtp.error as { message?: string } | null)?.message ??
            "Couldn't send the verification code. Try again."}
        </Alert>
      )}

      {verifyOtp.isError && (
        <Alert variant="destructive">
          {(verifyOtp.error as { message?: string } | null)?.message ??
            "That code didn't match. Try again."}
        </Alert>
      )}

      <OTPInput length={OTP_LENGTH} value={otp} onChange={setOtp} />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={handleSend}
          disabled={!canResend || sendOtp.isPending}
          className="text-xs font-medium text-rose underline-offset-4 hover:underline disabled:cursor-not-allowed disabled:text-navy2/40 disabled:no-underline"
        >
          {sendOtp.isPending
            ? "Sending..."
            : canResend
              ? "Resend code"
              : `Resend in ${Math.ceil(remainingMs / 1000)}s`}
        </button>

        <div className="flex items-center gap-2">
          {onCancel && (
            <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
              Cancel
            </Button>
          )}

          <Button
            type="button"
            size="sm"
            onClick={handleVerify}
            loading={verifyOtp.isPending}
            disabled={otp.length !== OTP_LENGTH}
          >
            Verify
          </Button>
        </div>
      </div>
    </div>
  );
}
